/** Мелкие переиспользуемые элементы интерфейса. */

import type { ChangeEvent, ReactNode } from 'react'

import { fieldLabel, statusLabel } from '../lib/format'

// ── кнопка ───────────────────────────────────────────────────────────────────

interface ButtonProps {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  busy?: boolean
  variant?: 'primary' | 'secondary' | 'ghost'
  block?: boolean
  type?: 'button' | 'submit'
  /** Разрушающее действие — подсвечивается красным. */
  danger?: boolean
}

export function Button({
  children,
  onClick,
  disabled,
  busy,
  variant = 'primary',
  block,
  type = 'button',
  danger,
}: ButtonProps) {
  const classes = ['btn']
  if (variant !== 'primary') classes.push(variant)
  if (block) classes.push('block')
  if (danger) classes.push('danger')
  return (
    <button
      type={type}
      className={classes.join(' ')}
      onClick={onClick}
      disabled={disabled || busy}
    >
      {busy && <span className="spinner" aria-hidden />}
      {children}
    </button>
  )
}

// ── поле ─────────────────────────────────────────────────────────────────────

interface FieldProps {
  name: string
  value: string
  onChange: (value: string) => void
  label?: string
  type?: 'text' | 'date' | 'email' | 'tel' | 'password'
  required?: boolean
  /** Поле распознано с низкой уверенностью — выделяется (п. 6.3 ТЗ). */
  lowConfidence?: boolean
  /** Текст ошибки валидации. */
  error?: string
  hint?: string
  placeholder?: string
  inputMode?: 'text' | 'numeric' | 'tel' | 'email'
  autoComplete?: string
  multiline?: boolean
  disabled?: boolean
}

export function Field({
  name,
  value,
  onChange,
  label,
  type = 'text',
  required,
  lowConfidence,
  error,
  hint,
  placeholder,
  inputMode,
  autoComplete,
  multiline,
  disabled,
}: FieldProps) {
  const classes = ['field']
  if (error) classes.push('invalid')
  else if (lowConfidence) classes.push('low-confidence')

  const handle = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange(event.target.value)

  const message =
    error ?? (lowConfidence ? 'Распознано неуверенно — проверьте значение' : hint)

  return (
    <div className={classes.join(' ')}>
      <label htmlFor={name}>
        {label ?? fieldLabel(name)}
        {required && <span className="required"> *</span>}
      </label>
      {multiline ? (
        <textarea
          id={name}
          name={name}
          value={value}
          onChange={handle}
          placeholder={placeholder}
          disabled={disabled}
        />
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={handle}
          placeholder={placeholder}
          inputMode={inputMode}
          autoComplete={autoComplete}
          disabled={disabled}
        />
      )}
      {message && <div className="hint">{message}</div>}
    </div>
  )
}

// ── выпадающий список ────────────────────────────────────────────────────────

export interface SelectOption {
  value: string
  label: string
}

export interface SelectGroup {
  label: string
  options: SelectOption[]
}

interface SelectProps {
  name: string
  value: string
  onChange: (value: string) => void
  /** Плоский список вариантов. Взаимоисключается с groups. */
  options?: SelectOption[]
  /**
   * Варианты, разбитые на группы (`<optgroup>`). Нужны там, где названия
   * повторяются и различаются только происхождением — например тариф «Дом 100»
   * есть в нескольких населённых пунктах, и цена подключения у них разная.
   */
  groups?: SelectGroup[]
  label?: string
  required?: boolean
  lowConfidence?: boolean
  error?: string
  hint?: string
  placeholder?: string
}

export function Select({
  name,
  value,
  onChange,
  options,
  groups,
  label,
  required,
  lowConfidence,
  error,
  hint,
  placeholder = '— выберите —',
}: SelectProps) {
  const classes = ['field']
  if (error) classes.push('invalid')
  else if (lowConfidence) classes.push('low-confidence')

  const message =
    error ?? (lowConfidence ? 'Распознано неуверенно — проверьте значение' : hint)

  return (
    <div className={classes.join(' ')}>
      <label htmlFor={name}>
        {label ?? fieldLabel(name)}
        {required && <span className="required"> *</span>}
      </label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{placeholder}</option>
        {options?.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
        {groups?.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {message && <div className="hint">{message}</div>}
    </div>
  )
}

// ── сообщения ────────────────────────────────────────────────────────────────

interface AlertProps {
  kind: 'error' | 'warn' | 'ok'
  title?: string
  children: ReactNode
  onRetry?: () => void
  retryLabel?: string
  onDismiss?: () => void
}

export function Alert({
  kind,
  title,
  children,
  onRetry,
  retryLabel = 'Повторить',
  onDismiss,
}: AlertProps) {
  return (
    <div className={`alert ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {title && <strong>{title}</strong>}
      {children}
      {(onRetry || onDismiss) && (
        <div className="alert-actions">
          {onRetry && (
            <Button variant="secondary" onClick={onRetry}>
              {retryLabel}
            </Button>
          )}
          {onDismiss && (
            <Button variant="ghost" onClick={onDismiss}>
              Закрыть
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export function StatusBadge({ status }: { status: string }) {
  return <span className={`badge ${status}`}>{statusLabel(status)}</span>
}

export function Loading({ text = 'Загрузка…' }: { text?: string }) {
  return (
    <div className="center">
      <span className="spinner" aria-hidden />
      <span>{text}</span>
    </div>
  )
}

// ── индикатор шагов ──────────────────────────────────────────────────────────

export const STEP_COUNT = 5

export function Steps({ current }: { current: number }) {
  return (
    <div className="steps" aria-label={`Шаг ${current} из ${STEP_COUNT}`}>
      {Array.from({ length: STEP_COUNT }, (_, index) => {
        const step = index + 1
        const state = step < current ? 'done' : step === current ? 'active' : ''
        return <div key={step} className={`step ${state}`} />
      })}
    </div>
  )
}
